import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import WideModal from "./WideModal";
import Button from "components/Button";
import { testRfqNotificationRule } from "actions/rfqNotifications";
import rfqNotificationsService from "services/rfqNotifications.service";
import { getApiErrorMessage } from "utils/apiErrors";
import RfqSampleTable from "./RfqSampleTable";
import styles from "./RuleTestModal.module.scss";

// AC-17: exact disclaimer wording (em-dash and surrounding spaces are intentional).
const TRUCK_FILTER_DISCLAIMER =
  "This count does not include the truck-availability filter — actual notifications may be fewer.";

// Inline style: this component's SCSS module is owned elsewhere, so the note
// uses a small inline informational-banner style that fits the modal.
const disclaimerStyle = {
  padding: "1rem 1.25rem",
  marginBottom: "1.5rem",
  borderRadius: "0.5rem",
  backgroundColor: "#fffbeb",
  border: "1px solid #fcd34d",
  color: "#92400e",
  fontSize: "0.95rem",
  lineHeight: 1.5,
};

const RuleTestModal = ({ isOpen, ruleId, onClose }) => {
  const dispatch = useDispatch();
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Whether the previewed rule has the truck-availability filter engaged. The
  // preview count never applies the truck gate, so when engaged we show a
  // disclaimer that the real notification count may be lower.
  const [truckFilterEngaged, setTruckFilterEngaged] = useState(false);

  const mountedRef = useRef(true);
  const requestSeqRef = useRef(0);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isOpen || !ruleId) {
      return;
    }

    let cancelled = false;
    handleTestRule();
    fetchTruckFilterEngagement(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [isOpen, ruleId]);

  const handleTestRule = async () => {
    const seq = ++requestSeqRef.current;
    const isStale = () => !mountedRef.current || requestSeqRef.current !== seq;
    setLoading(true);
    setError(null);
    setTestResults(null);

    try {
      const result = await dispatch(testRfqNotificationRule(ruleId));
      if (isStale()) return;
      setTestResults(result);
    } catch (err) {
      if (isStale()) return;
      setError(getApiErrorMessage(err) || "Failed to test rule. Please try again.");
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  const fetchTruckFilterEngagement = async (isCancelled) => {
    try {
      const details = await rfqNotificationsService.getRuleDetails(ruleId);
      if (isCancelled()) return;
      setTruckFilterEngaged(
        Boolean(details?.all_active_trucks || details?.trucks?.length > 0)
      );
    } catch (err) {
      if (isCancelled()) return;
      // Non-fatal: if rule details can't be loaded, simply omit the disclaimer.
      setTruckFilterEngaged(false);
    }
  };

  const handleClose = () => {
    setTestResults(null);
    setError(null);
    setLoading(false);
    setTruckFilterEngaged(false);
    onClose();
  };

  return (
    <WideModal isOpen={isOpen} onClose={handleClose}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Test Results for Rule #{ruleId}</h2>
        </div>

        {loading && (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Testing rule against historical data...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorMessage} role="alert">
              {error}
            </div>
            <Button onClick={() => handleTestRule()} className={styles.retryButton}>
              Retry Test
            </Button>
          </div>
        )}

        {testResults && (
          <div className={styles.results}>
            {truckFilterEngaged && (
              <div style={disclaimerStyle} role="note">
                {TRUCK_FILTER_DISCLAIMER}
              </div>
            )}
            <div className={styles.statsContainer}>
              <div className={styles.statsCard}>
                <h3 className={styles.statsTitle}>7 Days Statistics</h3>
                <div className={styles.statsNumber}>
                  {testResults.stats_7_days?.count || 0}
                </div>
                <p className={styles.statsLabel}>RFQs matched</p>
              </div>
              
              <div className={styles.statsCard}>
                <h3 className={styles.statsTitle}>30 Days Statistics</h3>
                <div className={styles.statsNumber}>
                  {testResults.stats_30_days?.count || 0}
                </div>
                <p className={styles.statsLabel}>RFQs matched</p>
              </div>
            </div>

            <div className={styles.samplesContainer}>
              <h3 className={styles.sectionTitle}>Sample RFQs (7 Days)</h3>
              {testResults.stats_7_days?.sample_rfqs?.length > 0 ? (
                <RfqSampleTable samples={testResults.stats_7_days.sample_rfqs} />
              ) : (
                <p className={styles.noSamples}>No sample RFQs found for the last 7 days.</p>
              )}
            </div>

            <div className={styles.samplesContainer}>
              <h3 className={styles.sectionTitle}>Sample RFQs (30 Days)</h3>
              {testResults.stats_30_days?.sample_rfqs?.length > 0 ? (
                <RfqSampleTable samples={testResults.stats_30_days.sample_rfqs} />
              ) : (
                <p className={styles.noSamples}>No sample RFQs found for the last 30 days.</p>
              )}
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <Button onClick={handleClose} className={styles.closeBtn}>
            Close
          </Button>
        </div>
      </div>
    </WideModal>
  );
};

export default RuleTestModal;